import { useEffect, useState } from 'react'
import { isRole, newDeploymentIdProblem, roleHasCapability } from '@qim/shared'
import { ApiError, clearBoundary, fetchBoundary, recordBoundary } from '../api.js'
import { Modal } from './modal.jsx'
import { formatInstant } from '../format.js'
import type { BoundaryView } from '../api.js'
import type { BoundaryMark, Capability } from '@qim/shared'
import type { JSX } from 'react'

/**
 * "Permissions boundary", on one instance's screen (IM-192).
 *
 * The customer creates the boundary in their own account; this hands them the
 * policy to create, checks that what they created is that policy, records it,
 * and from then on every run for the instance carries it. Qrvey never creates
 * or edits it.
 *
 * ## What it says without asking anyone
 *
 * Whether a boundary is recorded, from the instance's own record — the API's
 * table, not the customer's account — so the screen says what the next run
 * will carry at no cost.
 *
 * ## What it asks the account, and when
 *
 * The published document and the live policy are two reads into the
 * customer's account, so they are made when somebody opens the check, never on
 * render: the IM-83 rule, and why the check is a dialog that mounts only when
 * open (IM-146).
 *
 * ## Offered by capability, never by role name
 *
 * Recording is `deployment:create`, clearing `account:onboard`. Not a control —
 * the API checks both — but a button that always fails is a worse way to say no
 * than not offering it.
 */

const holds = (role: string, capability: Capability): boolean =>
  isRole(role) && roleHasCapability(role, capability)

/** Saves an artifact as a file, the way the other templates and the bundle do. */
const save = (filename: string, document: unknown): void => {
  const blob = new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = window.document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

const refusalSentence = (error: unknown): string => {
  if (error instanceof ApiError) {
    const body = error.body as { detail?: unknown } | undefined
    if (typeof body?.detail === 'string') return body.detail
    if (error.status === 403) return 'This role cannot do that here.'
  }
  return 'Could not reach the account just now.'
}

export const BoundaryPanel = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
  recorded,
  lastApplyAt,
  role,
  onChanged,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
  /** The standing boundary, from the instance's own record. */
  readonly recorded: BoundaryMark | undefined
  /**
   * When the newest apply that ran was requested, if one did. An `exact` boundary
   * names two roles exactly, so an apply after it was recorded may have
   * recreated one: said here, and checked when somebody opens the check —
   * never on render, which would be a call into the account per page view.
   */
  readonly lastApplyAt?: string
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element | null => {
  const [open, setOpen] = useState(false)
  if (!holds(role, 'account:read')) return null

  return (
    <section className="panel">
      <h2>Permissions boundary</h2>
      {recorded === undefined ? (
        <p className="muted">
          None recorded. Without one, the IAM roles this instance creates are limited only by what
          the deploy role allows them.
        </p>
      ) : (
        <p>
          Every run sends <code>{recorded.arn}</code> ({recorded.kind}), recorded by {recorded.by}{' '}
          on <time dateTime={recorded.at}>{formatInstant(recorded.at)}</time>.
        </p>
      )}
      {recorded !== undefined &&
      recorded.kind === 'exact' &&
      lastApplyAt !== undefined &&
      lastApplyAt > recorded.at ? (
        <p className="approval-note">
          An apply has finished since this boundary was recorded. An exact boundary goes out of date
          when an upgrade recreates one of the roles it names — check that it still matches.
        </p>
      ) : null}
      <div className="actions">
        <button type="button" className="ghost" onClick={() => setOpen(true)}>
          {recorded === undefined ? 'Set up a boundary' : 'Check the boundary'}
        </button>
      </div>
      {open ? (
        <Modal title="Permissions boundary" onCancel={() => setOpen(false)} wide>
          <BoundaryCheck
            baseUrl={baseUrl}
            tenantId={tenantId}
            accountKey={accountKey}
            deploymentId={deploymentId}
            role={role}
            onChanged={onChanged}
          />
        </Modal>
      ) : null}
    </section>
  )
}

/** The dialog's body: asks the account once when mounted, and again after a change. */
export const BoundaryCheck = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
  region,
  role,
  onChanged,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
  /**
   * Only for an instance that does not exist yet (the run form): the region its
   * first apply will build it in, which its boundary names.
   */
  readonly region?: string
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element => {
  const [view, setView] = useState<BoundaryView | undefined>(undefined)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<string | undefined>(undefined)
  const [confirmingClear, setConfirmingClear] = useState(false)
  const [asked, setAsked] = useState(0)

  useEffect(() => {
    let live = true
    setFailure(undefined)
    fetchBoundary(baseUrl, tenantId, accountKey, deploymentId, region)
      .then((answer) => {
        if (live) setView(answer)
      })
      .catch((error: unknown) => {
        if (live) setFailure(refusalSentence(error))
      })
    return () => {
      live = false
    }
  }, [baseUrl, tenantId, accountKey, deploymentId, region, asked])

  const act = async (what: 'record' | 'clear'): Promise<void> => {
    setBusy(true)
    setOutcome(undefined)
    try {
      if (what === 'record') {
        await recordBoundary(baseUrl, tenantId, accountKey, deploymentId, region)
        setOutcome('Recorded. Every run for this instance now carries it.')
      } else {
        const answer = await clearBoundary(baseUrl, tenantId, accountKey, deploymentId)
        setOutcome(answer.note ?? 'Cleared.')
      }
      setConfirmingClear(false)
      onChanged()
      setAsked((n) => n + 1)
    } catch (error: unknown) {
      setOutcome(refusalSentence(error))
    } finally {
      setBusy(false)
    }
  }

  if (failure !== undefined) return <p className="failure">{failure}</p>
  if (view === undefined) return <p className="muted">Asking the account…</p>

  const refused = view.check.state === 'refused' ? view.check : undefined
  const canRecord = holds(role, 'deployment:create')
  const canClear = holds(role, 'account:onboard')
  const recordedMatches = view.recorded !== null && view.check.state === 'match'

  return (
    <div className="boundary-check">
      <p>
        This instance’s boundary is <code>{view.expectedArn}</code>: a policy you create and own,
        which the installer attaches to every IAM role of the instance. Qrvey never creates or edits
        it.
      </p>

      {view.offer === undefined ? null : (
        <>
          <h3>Create it from one of these</h3>
          {view.offer.source === 'rendered' ? (
            <p className="muted">
              Rendered here from the installer’s boundary template for {view.region}, because this
              instance has not been applied yet. Record it before the first apply and every role is
              created already bounded.
            </p>
          ) : null}
          {view.template.matches === false ? (
            /* Nothing to do for this instance — its published document is what is
              offered — but a new one rendered here could fail the installer's own
              pre-apply check until the pinned copy is updated. */
            <p className="failure">
              The installer that last applied this instance rendered its boundary from a different
              template than this console pins. Its published document is what is offered here; a new
              instance rendered by this console may not match until the pinned template is updated.
            </p>
          ) : null}
          <p className="muted">
            {view.offer.kind === 'exact'
              ? 'An exact boundary: it names two of this instance’s roles by their exact names, so an upgrade that recreates either one makes it out of date.'
              : 'A pattern boundary: it names this instance’s roles by patterns carrying its id, so an upgrade does not make it out of date.'}
          </p>
          <div className="actions">
            <button
              type="button"
              className="ghost"
              onClick={() => {
                if (view.offer !== undefined)
                  save(view.offer.artifact.filename, view.offer.artifact.document)
              }}
            >
              Download the stack template
            </button>
          </div>
          <p className="muted">Or with the CLI, after saving the document below to that file:</p>
          <pre className="boundary-command">{view.offer.command}</pre>
          <details>
            <summary>The policy document</summary>
            <pre className="boundary-document">{JSON.stringify(view.offer.document, null, 2)}</pre>
          </details>
        </>
      )}

      <h3>What the account holds</h3>
      {view.check.state === 'match' ? (
        <p role="status">
          {recordedMatches
            ? 'Recorded, and the policy in the account still matches.'
            : 'The policy in the account matches what this instance expects.'}
        </p>
      ) : (
        <div role="status">
          <p className={refused?.error === 'boundary-not-published' ? 'muted' : 'failure'}>
            {refused?.detail}
          </p>
          {refused?.differences === undefined ? null : (
            <ul>
              {refused.differences.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
          {/* A recorded boundary that no longer matches still goes out on every
            run — the installer warns, and a stale one can refuse Karpenter its
            PassRole. Said here, where the fix is. */}
          {view.recorded !== null && refused?.error === 'boundary-mismatch' ? (
            <p className="failure">
              The recorded boundary is out of date: update the policy in the account from the
              document above.
            </p>
          ) : null}
        </div>
      )}

      <div className="actions">
        {canRecord && view.check.state === 'match' && !recordedMatches ? (
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => void act('record')}
          >
            Record this boundary
          </button>
        ) : null}
        {canClear && view.recorded !== null && !confirmingClear ? (
          <button
            type="button"
            className="ghost"
            disabled={busy}
            onClick={() => setConfirmingClear(true)}
          >
            Clear the boundary…
          </button>
        ) : null}
      </div>
      {confirmingClear ? (
        <div className="approval-note">
          <p>
            Clearing stops runs carrying this boundary. With an administrator deploy role the next
            apply removes it from every role; with a scoped one, removing a boundary is refused, so
            the next apply fails until one is recorded again.
          </p>
          <div className="actions">
            <button
              type="button"
              className="danger"
              disabled={busy}
              onClick={() => void act('clear')}
            >
              Clear it
            </button>
            <button type="button" className="ghost" onClick={() => setConfirmingClear(false)}>
              Keep it
            </button>
          </div>
        </div>
      ) : null}
      {outcome === undefined ? null : <p role="status">{outcome}</p>}
    </div>
  )
}

/**
 * The boundary of an instance that does not exist yet, on the run form
 * (IM-192 slice 2).
 *
 * A new instance's boundary has to be recorded before its first apply, so that
 * apply creates every role already bounded — and the boundary is named for the
 * instance and its region, so both have to be decided first. That is why the
 * form can mint an id here rather than leaving it to the API: an id the API
 * mints at dispatch is one nobody could have recorded a boundary for.
 *
 * Optional, and asked of the account only when somebody opens it: the
 * IM-83 rule, which a `<details>` would break by mounting its children closed.
 */
export const NewInstanceBoundary = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
  region,
  role,
  onGenerateId,
  onRecorded,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  /** As typed, trimmed. */
  readonly deploymentId: string
  readonly region: string | undefined
  readonly role: string
  readonly onGenerateId: () => void
  /** Told when a boundary is recorded, so the form can ask its preflight again. */
  readonly onRecorded?: () => void
}): JSX.Element | null => {
  const [openFor, setOpenFor] = useState<string | undefined>(undefined)
  if (!holds(role, 'account:read')) return null
  // The id field already says what is wrong with an id outside the grammar.
  if (deploymentId !== '' && newDeploymentIdProblem(deploymentId) !== undefined) return null
  const asking = region === undefined ? undefined : `${deploymentId}@${region}`

  return (
    <div className="field" role="group" aria-labelledby="runBoundaryNew-label">
      <label id="runBoundaryNew-label">Permissions boundary (optional)</label>
      {deploymentId === '' ? (
        <p className="hint">
          A boundary is recorded before the first apply and named for the instance, so it needs the
          instance’s id first.{' '}
          <button type="button" className="ghost" onClick={onGenerateId}>
            Generate an id
          </button>
        </p>
      ) : region === undefined ? (
        <p className="hint">Choose the region first: the boundary names it.</p>
      ) : openFor === asking ? (
        <BoundaryCheck
          baseUrl={baseUrl}
          tenantId={tenantId}
          accountKey={accountKey}
          deploymentId={deploymentId}
          region={region}
          role={role}
          onChanged={onRecorded ?? (() => undefined)}
        />
      ) : (
        <p className="hint">
          Record one now and every IAM role this instance creates is bounded from its first apply.{' '}
          <button type="button" className="ghost" onClick={() => setOpenFor(asking)}>
            Set up a boundary for {deploymentId}
          </button>
        </p>
      )}
    </div>
  )
}
